/**
 * ACT2-1108: which repositories the repo picker suggests first — the ones whose name looks like a dbt or
 * RudderStack Profiles project. A workspace can see a thousand repositories, so the few likely ones go on
 * top. Only the repository's own name is read (not the org, not its contents): an org called "analytics"
 * would otherwise suggest every repo it owns.
 */

/** At most this many suggestions. */
export const MAX_SUGGESTED_REPOS = 5;

/** Strong hints name the tool; weak hints name the domain. A strong match ranks above a weak one. */
const STRONG_HINTS: readonly RegExp[] = [/dbt/, /profiles?/, /^pb[-_]/, /[-_]pb$/];
const WEAK_HINTS: readonly RegExp[] = [/analytics/, /warehouse/];

/** 2 for a tool name, 1 for a domain word, 0 for neither. */
export function repoSuggestionScore(fullName: string): number {
  const name = (fullName.split('/').pop() ?? fullName).toLowerCase();
  if (STRONG_HINTS.some((hint) => hint.test(name))) return 2;
  if (WEAK_HINTS.some((hint) => hint.test(name))) return 1;
  return 0;
}

/** The repositories to suggest, best first; ties keep the list's own order. */
export function suggestRepos<T extends { fullName: string }>(repos: readonly T[], max = MAX_SUGGESTED_REPOS): T[] {
  return repos
    .map((repo, index) => ({ repo, index, score: repoSuggestionScore(repo.fullName) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, max)
    .map((entry) => entry.repo);
}
